// === AgentLayer.jsx ===
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';

import SideBarComponent from './AgentComponent/SideBarComponent';
import HeaderComponent from './AgentComponent/HeaderComponent';
import TabComponent from './AgentComponent/TabComponent';
import ContentPanel from './AgentComponent/ContentPannel';
import RightPanel from './AgentComponent/RightPannel';
import AddAgentModal from './child/AddAgentModal';

import { fetchAssistants, fetchAssistantDetail } from '../store/app/assistantSlice';
import { User } from 'lucide-react';

const TABS = ['Agent', 'LLM', 'Transcriber', 'Voice', 'Summary', 'Evaluation', 'StructureData'];

export default function AgentLayer() {
  const dispatch = useDispatch();
  const { id: urlAssistantId } = useParams();

  const assistants = useSelector(state => state.assistants.value);
  const assistantDetail = useSelector(state => state.assistants.activeDetail);

  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [language, setLanguage] = useState('en-US');
  const [voiceName, setVoiceName] = useState('wendy');
  const [activeAssistantId, setActiveAssistantId] = useState(null);
  const [showAddAgentModal, setShowAddAgentModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const userOrgId = useSelector((state) => state?.user?.user?.organizationId);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      if (userOrgId) {
        await dispatch(fetchAssistants(userOrgId));
      }
      setIsLoading(false);
    };
    loadData();
  }, [dispatch, userOrgId]);

  useEffect(() => {
    if (urlAssistantId) {
      setActiveAssistantId(urlAssistantId);
    }
  }, [urlAssistantId]);

  useEffect(() => {
    const fetchData = async () => {
      if (activeAssistantId) {
        setIsLoading(true);
        await dispatch(fetchAssistantDetail(activeAssistantId));
        setIsLoading(false);
      }
    };
    fetchData();
  }, [activeAssistantId, dispatch]);

  useEffect(() => {
    if (!urlAssistantId && assistants.length > 0) {
      const defaultId = assistants[0].id;
      setActiveAssistantId(defaultId);
    }
  }, [assistants, urlAssistantId]);

  const handleSelectAssistant = useCallback((id) => {
    setActiveAssistantId(id);
  }, []);

  const handleSetActiveTab = () => setActiveTab(TABS[0]);

  const costData = useMemo(() => ([
    { label: 'Transcriber', value: '$0.006', color: '#4caf50' },
    { label: 'LLM', value: '$0.003', color: '#2196f3' },
    { label: 'Voice', value: '$0.030', color: '#ff9800' },
    { label: 'Platform', value: '$0.020', color: '#ffc107' },
  ]), []);

  const activeAssistant = useMemo(() => assistants.find(a => a.id === activeAssistantId)?.name || '', [assistants, activeAssistantId]);
  // console.log(assistants)
  if (assistants.length === 0) {
    return (
      <motion.div className="agent-layer-container flex" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} style={{ height: '80vh', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '32px 16px', color: '#4B5563', fontSize: '14px', width: '100%', maxWidth: '600px' }}>
          <div style={{ width: '72px', height: '72px', border: '2px dotted #d1d5db', backgroundColor: '#f9fafb', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
            <User size={40} color="#9ca3af" />
          </div>
          <div style={{ textAlign: 'left', width: '100%' }}>
            <h5 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Assistants</h5>
            <p style={{ color: '#6B7280', marginBottom: '16px', lineHeight: '1.5' }}>
              Assistants are voice AI chat bots used for integrations into your applications.<br />
              You can fully configure them to your business's needs, and we support all major models and providers.
            </p>
            <button className="btn btn-primary" style={{ padding: '0.4rem 0.75rem' }} onClick={() => setShowAddAgentModal(true)}>
              + Create Assistant
            </button>
          </div>
        </div>
        {showAddAgentModal && <AddAgentModal handleCancel={() => setShowAddAgentModal(false)} />}
      </motion.div>
    );
  }

  return (
    <motion.div className="agent-layer-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
      <SideBarComponent
        assistants={assistants}
        activeAssistantId={activeAssistantId}
        onSelectAssistant={handleSelectAssistant}
        handleSetActiveTab={handleSetActiveTab}
      />

      <main className="agent-main">
        <HeaderComponent
          activeAssistant={activeAssistant}
          activeAssistantId={activeAssistantId}
          language={language}
          onChangeLanguage={setLanguage}
          voiceName={voiceName}
          onChangeVoice={setVoiceName}
          costData={costData}
        />

        <TabComponent tabs={TABS} activeTab={activeTab} onTabClick={setActiveTab} />

        <section className="agent-content">
          <ContentPanel activeTab={activeTab} assistantDetail={assistantDetail?.assistant} />
          <RightPanel assistantId={activeAssistantId} />
        </section>
      </main>

      {showAddAgentModal && <AddAgentModal handleCancel={() => setShowAddAgentModal(false)} />}

      {isLoading && (
        <>
          <style>
          </style>
          <div className='flex'
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
            }}
          >
            <div
              style={{
                border: '6px solid #d4f8d4',
                borderTop: '6px solid #28a745',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                animation: 'greenSpin 1s linear infinite',
              }}
            />
          </div>
        </>
      )}

    </motion.div>
  );
}